import type { AirdropFarmingPairData } from "@cede/types";
import { getBaseToken, getQuoteToken } from "@cede/utils";
import type { PerpMarket } from "@/hooks/airdrop-farming/types";
import { MarketType } from "@cede/trading-data";
import { assignMarketCategories } from "@/utils/trading";

// ── Backend-sourced adapter ──────────────────────────────────────────────────

/**
 * Maps backend-scored pairs (from suggest-pairs endpoint) to PerpMarket[].
 *
 * The backend worker pre-fetches Hibachi market data, scores pairs by orderbook
 * quality, and caches the results. This function normalizes those scored pairs
 * to the generic PerpMarket format consumed by the UI.
 */
export function mapScoredPairsToPerpMarkets(pairs: AirdropFarmingPairData[]): PerpMarket[] {
  return pairs.map((pair): PerpMarket => {
    const base = pair.baseAsset || getBaseToken(pair.symbol);
    const quoteAsset = getQuoteToken(pair.symbol);

    return {
      type: MarketType.PERP,
      symbol: pair.symbol,
      pair: pair.symbol, // Already in CCXT format from backend (e.g. "BTC/USDT:USDT")
      baseAsset: base,
      quoteAsset,
      markPrice: pair.markPrice,
      volume24h: pair.volume24hRaw,
      priceChange24h: pair.priceChange24h,
      openInterest: pair.openInterest ?? "0",
      maxLeverage: pair.maxLeverage,
      isGrowthMode: pair.isGrowthMode,
      onlyIsolated: false,
      isDelisted: false,
      searchString: `${base.toLowerCase()}perp`,
      categories: assignMarketCategories(base, MarketType.PERP),
    };
  });
}
